import { AxiosError } from 'axios';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import {
  useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Row, Col,
} from 'reactstrap';

import { IRootState } from '@app/store';
import { sendVerificationCodeRequest, verifyCodeRequest } from '@reducers/auth/AuthAction';
import { handleError } from '@services/ErrorHandler';
import LocaleService from '@services/LocaleService';
import { CustomButton } from '@shared/buttons';
import AsyncButtonLink from '@shared/buttons/AsyncButtonLink/AsyncButtonLink';
import InputField from '@shared/components/input/InputField';
import { VERIFICATION_TYPES } from '@shared/constants';
import { formatPhoneNumber } from '@shared/helpers';
import useTranslation from '@shared/hooks/useTranslation';
import {
  CloseIcon, BackIcon,
} from '@shared/icons';

type IVerificationContent = {
  onNext: () => void;
  onBack: () => void;
  onToggle: () => void;
  type?: string;
};

const VerifyCodeContent = (props: IVerificationContent) => {
  const i18n = useTranslation('general');
  const i18nVerification = useTranslation('verification');
  const {
    onNext, onBack, onToggle, type,
  } = props;
  const [ otp, setOtp ] = useState('');
  const [ isVerifying, setVerifying ] = useState(false);
  const [ otpError, setOtpError ] = useState('');
  const [ timer, setTimer ] = useState(0);
  const dispatch = useDispatch<any>();
  const {
    codeValidityInSeconds, emailOrMobile, timestamp,
  } = useSelector(({ Auth }: IRootState) => ({
    codeValidityInSeconds: Auth.codeValidityInSeconds,
    emailOrMobile: Auth.registration.emailOrMobile,
    timestamp: Auth.registration.timestamp,
  }));

  const handleVerify = async (e?: React.FormEvent<HTMLFormElement>
  | React.ClipboardEvent<HTMLDivElement>) => {
    e?.preventDefault();
    setVerifying(true);
    setOtpError('');
    try {
      const data = {
        email_or_mobile: emailOrMobile,
        verification_code: otp,
        type: VERIFICATION_TYPES.registration,
        payload_type: type,
      };
      await dispatch(verifyCodeRequest(data)).$promise;
      setOtpError('');
      onNext();
    } catch (error) {
      const { response: errorResponse } = error as AxiosError;
      if (errorResponse) {
        setOtpError(errorResponse.data?.message);
        if (errorResponse?.status === 422) {
          setOtp('');
        }
        return;
      }
      handleError(error);
    } finally {
      setVerifying(false);
    }
  };

  const getTime = () => {
    const time = (moment(timestamp).unix() + codeValidityInSeconds) - moment().unix();
    setTimer(time);
  };

  useEffect(() => {
    const interval = setInterval(() => getTime(), 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ timestamp ]);

  const handleResend = async () => {
    setOtpError('');
    try {
      const data = {
        emailOrMobile,
        email_or_mobile: type === 'mobile' ? emailOrMobile?.replace('+1', '') : emailOrMobile,
        type: VERIFICATION_TYPES.registration,
        payload_type: type,
      };
      await dispatch(sendVerificationCodeRequest(data)).$promise;
    } catch (error) {
      const { response: errorResponse } = error as AxiosError;
      if (errorResponse) {
        setOtpError(errorResponse.data?.message);
      }
      handleError(error);
    }
  };

  const countdownMins = Math.floor(timer / 60);
  const countdownSecs = timer % 60;

  const isVerifyDisabled = useMemo(() => (
    isEmpty(otp) || (!isEmpty(otp) && otp.length !== 6)),
  [ otp ]);

  return (
    <div className="verification-code-content-container">
      <Row className="mb-3">
        <Col xs={6}>
          <CustomButton
            id="btn-back"
            className="btn-lg verify-back-btn"
            color="secondary"
            onClick={onBack}
          >
            <BackIcon /> {i18n.button.back}
          </CustomButton>
        </Col>
        <Col xs={6} className="fp-close-button-style">
          <a aria-label="Close" role="button" href="#" onClick={onToggle}><CloseIcon /></a>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="s2 verification-code-modal-title mb-5">{i18nVerification.title}</div>
          <p className="b1 mb-2">
            {LocaleService.parseTranslationElement(i18nVerification.description, {
              // eslint-disable-next-line react/no-unstable-nested-components
              emailOrMobile: () => (
                <span className="b2">
                  {type === 'mobile' ? formatPhoneNumber(emailOrMobile) : emailOrMobile}
                </span>
              ),
            })}
          </p>
        </Col>
      </Row>
      <Form id="verification-form" noValidate onSubmit={handleVerify}>
        <InputField
          id="verification-code"
          label={i18nVerification.input.otp.label}
          placeholder={i18nVerification.input.otp.placeholder}
          onInputChangeHandler={(value) => setOtp(value.trim())}
          value={otp}
          type="text"
          name="otp"
          fieldError={otpError}
        />
        <div className="button-container">
          <CustomButton
            id="btn-verify"
            type="submit"
            className="btn-lg w-100"
            loading={isVerifying}
            disabled={isVerifyDisabled}
            color="primary"
          >{i18n.button.verify}
          </CustomButton>
        </div>
        <div className="p1 text-center resend-container">
          <span className="align-middle">{i18nVerification.resend} </span>
          {timer < 0 || (Number.isNaN(countdownMins) && Number.isNaN(countdownSecs)) ? <AsyncButtonLink onClick={handleResend}>{i18nVerification.button.resend_code}</AsyncButtonLink> : <span className="timer">{countdownMins}:{(`0${countdownSecs}`).slice(-2)}</span>}
        </div>
      </Form>
    </div>
  );
};

export default VerifyCodeContent;
